import {
  adjustFromTimeZone,
  adjustToTimeZone,
  rrcWasteTypes,
  rrcWastePhysicalCharacteristics,
  rrcWasteClassifications,
  rrcWasteBasisTypes,
  type RrcEp9WasteProfile,
  type RrcEp9WasteProfileCreateDTO,
} from '@ots-frontend/ots-data';
import { rrcProfileDate } from '../rrc-profile-ui';
export const ep9WasteProfileDate = rrcProfileDate;
export const ep9WasteOptions = () =>
  Object.fromEntries(
    Object.entries({
      wasteType: rrcWasteTypes,
      wastePhysicalCharacteristics: rrcWastePhysicalCharacteristics,
      wasteClassification: rrcWasteClassifications,
      wasteBasis: rrcWasteBasisTypes,
    }).map(([key, codes]) => [
      key,
      codes.map((code) => ({
        value: code.code,
        label: code.displayValue.defaultMessage ?? code.code,
      })),
    ])
  ) as Record<
    | 'wasteType'
    | 'wastePhysicalCharacteristics'
    | 'wasteClassification'
    | 'wasteBasis',
    { value: string; label: string }[]
  >;
export const ep9WasteCodeLabel = (code?: string) =>
  Object.values(ep9WasteOptions())
    .flat()
    .find((option) => option.value === code)?.label ??
  code ??
  'Not specified';
export type Ep9WasteDraft = {
  objectId: string;
  objectType: string;
  start?: number;
  end?: number;
  wasteType: string;
  wastePhysicalCharacteristics: string;
  wasteClassification: string;
  wasteBasis: string;
  wasteChemicalCharacteristics: string;
  wasteConstituents: string;
  wasteNormReading: string;
  rcraExempt: boolean;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
};
export type Ep9WasteErrors = Partial<Record<keyof Ep9WasteDraft, string>>;
export const toEp9WasteDraft = (
  profile?: Partial<RrcEp9WasteProfile>
): Ep9WasteDraft => ({
  objectId: profile?.objectId ?? '',
  objectType: profile?.objectType ?? 'COMPANY',
  start:
    profile?.effectiveStartDttm == null
      ? undefined
      : adjustFromTimeZone(profile.effectiveStartDttm).getTime(),
  end:
    profile?.effectiveEndDttm == null
      ? undefined
      : adjustFromTimeZone(profile.effectiveEndDttm).getTime(),
  wasteType: profile?.wasteType ?? '',
  wastePhysicalCharacteristics: profile?.wastePhysicalCharacteristics ?? '',
  wasteClassification: profile?.wasteClassification ?? '',
  wasteBasis: profile?.wasteBasis ?? '',
  wasteChemicalCharacteristics: profile?.wasteChemicalCharacteristics ?? '',
  wasteConstituents: profile?.wasteConstituents ?? '',
  wasteNormReading:
    profile?.wasteNormReading == null ? '' : String(profile.wasteNormReading),
  rcraExempt: profile?.rcraExempt ?? false,
  contactName: profile?.contactName ?? '',
  contactPhone: profile?.contactPhone ?? '',
  contactEmail: profile?.contactEmail ?? '',
});
export const validateEp9Waste = (draft: Ep9WasteDraft): Ep9WasteErrors => {
  const errors: Ep9WasteErrors = {};
  for (const key of [
    'objectId',
    'wasteType',
    'wastePhysicalCharacteristics',
    'wasteClassification',
    'wasteBasis',
    'wasteChemicalCharacteristics',
    'wasteConstituents',
  ] as const)
    if (!draft[key].trim()) errors[key] = 'Required';
  if (!['COMPANY', 'SITE', 'WELL'].includes(draft.objectType))
    errors.objectType = 'Choose a company, site, or well.';
  if (draft.start == null || !Number.isFinite(draft.start))
    errors.start = 'An effective start date is required.';
  if (
    draft.end != null &&
    (!Number.isFinite(draft.end) ||
      (draft.start != null && draft.end <= draft.start))
  )
    errors.end = 'The end must be after the start.';
  if (
    draft.wasteNormReading.trim() &&
    (!Number.isFinite(Number(draft.wasteNormReading)) ||
      Number(draft.wasteNormReading) < 0)
  )
    errors.wasteNormReading = 'Enter a nonnegative NORM reading.';
  if (
    draft.contactEmail &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim())
  )
    errors.contactEmail = 'Enter a valid email address.';
  return errors;
};
/** Generator snapshots, waste IDs, approval, and version metadata are always resolved by the server. */
export const toEp9WastePayload = (
  draft: Ep9WasteDraft,
  id?: string
): RrcEp9WasteProfileCreateDTO & { id?: string } => ({
  ...(id ? { id } : {}),
  objectId: draft.objectId,
  objectType: draft.objectType,
  effectiveStartDttm: adjustToTimeZone(draft.start!).getTime(),
  effectiveEndDttm:
    draft.end == null ? undefined : adjustToTimeZone(draft.end).getTime(),
  wasteType: draft.wasteType,
  wastePhysicalCharacteristics: draft.wastePhysicalCharacteristics,
  wasteClassification: draft.wasteClassification,
  wasteBasis: draft.wasteBasis,
  wasteChemicalCharacteristics: draft.wasteChemicalCharacteristics.trim(),
  wasteConstituents: draft.wasteConstituents.trim(),
  wasteNormReading: draft.wasteNormReading.trim()
    ? Number(draft.wasteNormReading)
    : undefined,
  rcraExempt: draft.rcraExempt,
  contactName: draft.contactName.trim() || undefined,
  contactPhone: draft.contactPhone.trim() || undefined,
  contactEmail: draft.contactEmail.trim() || undefined,
});
